// FullPdfViewer.tsx
import React, { useEffect, useState, useRef } from 'react';
import * as pdfjs from 'pdfjs-dist';
// If you have a worker file at `/public/pdf.worker.js`, set it up like so:
// @ts-ignore
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.js';

interface FullPdfViewerProps {
  pdfUrl: string;
}

/**
 * A small helper component that renders a *single* PDF page into a canvas.
 */
const SinglePageView: React.FC<{
  pageProxy: pdfjs.PDFPageProxy;
  scale: number;
}> = ({ pageProxy, scale }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const renderPage = async () => {
      try {
        const viewport = pageProxy.getViewport({ scale });
        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await pageProxy.render({
          canvasContext: context!,
          viewport,
        }).promise;
      } catch (error) {
        console.error('Error rendering page:', error);
      }
    };

    renderPage();
  }, [pageProxy, scale]);

  return <canvas ref={canvasRef} style={{ marginBottom: '24px' }} />;
};

/**
 * Renders *all* pages of a PDF by first fetching the PDF, then storing
 * each PDFPageProxy in state, and finally mapping to <SinglePageView>.
 */
export const FullPdfViewer: React.FC<FullPdfViewerProps> = ({ pdfUrl }) => {
  const [pageProxies, setPageProxies] = useState<pdfjs.PDFPageProxy[]>([]);

  useEffect(() => {
    let isMounted = true;

    const fetchAndPreparePages = async () => {
      try {
        // Clear out any old pages
        setPageProxies([]);

        // Get the entire PDF
        const pdfDoc = await pdfjs.getDocument(pdfUrl).promise;
        const numPages = pdfDoc.numPages;
        const proxies: pdfjs.PDFPageProxy[] = [];

        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
          const page = await pdfDoc.getPage(pageNum);
          proxies.push(page);
        }

        // Only update state if the component is still mounted
        if (isMounted) {
          setPageProxies(proxies);
        }
      } catch (error) {
        console.error('Error fetching/rendering PDF:', error);
      }
    };

    fetchAndPreparePages();

    // Cleanup in case component unmounts mid-process
    return () => {
      isMounted = false;
    };
  }, [pdfUrl]);

  return (
    <div>
      {pageProxies.map((pageProxy, idx) => (
        <SinglePageView key={idx} pageProxy={pageProxy} scale={1.2} />
      ))}
    </div>
  );
};

export default FullPdfViewer;
